import { Component, OnInit, AfterViewInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AuthService } from '../../../core/abstraction/auth.service';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { AuthenticationService } from '../../services/authentication.service';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { ErrorHandlingService } from 'src/app/pages/core/services/utils/error-handling.service';
import { Observable, Subscription } from 'rxjs';
import { AUTH_ERROR } from 'src/app/pages/core/models/User';
import { SigninQrDialogComponent } from '../../components/signin-qr-dialog/signin-qr-dialog.component';
import { IGenerateQrResponse } from '../../models/Auth';
import { CrossLoginService } from '../../services/cross-login.service';
import { AuthUtilsService } from '../../utils/auth-utils.service';
import { GoogleAnalyticsService } from 'src/app/utils/google-analytics.service';
import {
  AlertDialogComponent,
  AlertDialogData,
} from 'src/app/modules/shared/components/dialogs/alert-dialog/alert-dialog.component';
import { Crisp } from 'crisp-sdk-web';

@Component({
  selector: 'app-signin',
  templateUrl: './signin.component.html',
  styleUrls: ['./signin.component.scss'],
})
export class SigninComponent implements OnInit, AfterViewInit, OnDestroy {
  loading = false;
  rerouteUrl: string;
  seePassword = false;
  actualYear: number = new Date().getFullYear();
  qrGenerated = false;
  recaptchaSubscription;
  forgotPassword: boolean;
  platform: string;
  platformToken: string;
  signInForm: FormGroup = this.fb.group({
    emailControl: ['', [Validators.required, Validators.email]],
    passwordControl: ['', [Validators.required, Validators.minLength(8)]],
  });

  constructor(
    private fb: FormBuilder,
    public authService: AuthService,
    private authentication: AuthenticationService,
    public router: Router,
    public dialog: MatDialog,
    private route: ActivatedRoute,
    private crossLoginService: CrossLoginService,
    private recaptchaV3Service: ReCaptchaV3Service,
    private errorHandling: ErrorHandlingService,
    private googleAnalyticsService: GoogleAnalyticsService,
    public utils: AuthUtilsService,
  ) {
    if (
      router.getCurrentNavigation().extras.state &&
      router.getCurrentNavigation().extras.state?.routeUrl
    ) {
      this.rerouteUrl = router.getCurrentNavigation().extras.state.routeUrl;
    }
  }

  ngOnInit() {
    this.forgotPassword = false;
    this.route.queryParams.subscribe((params) => {
      this.platform = params?.platform;
      this.platformToken = params?.token;
    });
    Crisp.setVacationMode(true);
  }

  ngAfterViewInit() {
    const element = document.getElementsByClassName('grecaptcha-badge')[0] as HTMLElement;
    if (element) {
      element.style.visibility = 'visible';
    }
  }

  ngOnDestroy() {
    if (this.recaptchaSubscription) {
      (this.recaptchaSubscription as Subscription).unsubscribe();
    }
    const element = document.getElementsByClassName('grecaptcha-badge')[0] as HTMLElement;
    if (element) {
      element.style.visibility = 'hidden';
    }
  }

  login() {
    this.seePassword = false;
    if (this.signInForm.invalid) {
      this.utils.touchControls(this.signInForm);
      return;
    }

    this.loading = true;
    this.recaptchaSubscription = this.recaptchaV3Service.execute('importantAction').subscribe(
      (token: string) => {
        this.authentication
          .login(
            (this.signInForm.controls.emailControl.value as string).trim(),
            (this.signInForm.controls.passwordControl.value as string).trim(),
            token,
          )
          .subscribe({
            next: (user) => {
              Crisp.setVacationMode(false);
              if (this.platform && !user.body.user.isCommerce) {
                let err = {
                  error: { internalStatus: AUTH_ERROR.USER_NOT_COMMERCE },
                };
                this.dialog.open<AlertDialogComponent, AlertDialogData>(AlertDialogComponent, {
                  panelClass: 'modal-container',
                  data: this.errorHandling.handleAuthErrors(err),
                });
                return;
              }

              if (this.platform === 'vtex' && this.platformToken) {
                this.authentication.loginVtex(this.platformToken).subscribe();
                return;
              }

              if (this.rerouteUrl) {
                this.router.navigateByUrl(this.rerouteUrl);
              } else {
                this.router.navigate(['core']);
              }
            },
            error: (err) => {
              this.loading = false;
              if (err.status !== 428) {
                this.dialog.open<AlertDialogComponent, AlertDialogData>(AlertDialogComponent, {
                  panelClass: 'modal-container',
                  data: this.errorHandling.handleAuthErrors(err),
                });
              }
            },
          });
      },
      (error) => {
        this.loading = false;
      },
    );
  }

  signInQR() {
    if (this.qrGenerated) return;
    this.qrGenerated = true;
    this.recaptchaV3Service.execute('importantAction').subscribe((humanToken: string) => {
      if (!humanToken) return;

      this.generateCrossLogin(humanToken).subscribe(
        ({ token, encodedToken, secondsToExpire }: IGenerateQrResponse) => {
          this.openQRDialog(encodedToken, secondsToExpire);

          this.crossLoginService.initializeSigninQr(token).subscribe((user) => {
            if (user) {
              this.router.navigate(['core']);
              this.crossLoginService.disconnectQRSocket();
              this.dialog.closeAll();
            }
          });
        },
      );
    });
  }

  generateCrossLogin(humanToken: string): Observable<IGenerateQrResponse> {
    return this.authentication.crossLogin(humanToken);
  }

  openQRDialog(encodedToken: string, secondsToExpire: number) {
    this.dialog
      .open(SigninQrDialogComponent, {
        data: {
          encodedToken,
          secondsToExpire,
        },
        maxWidth: 'auto',
        panelClass: 'qr-signin-dialog',
      })
      .afterClosed()
      .subscribe((closed) => {
        this.crossLoginService.disconnectQRSocket();
        this.qrGenerated = false;

        if (closed === 'EXPIRED TOKEN') {
          this.dialog
            .open<AlertDialogComponent, AlertDialogData>(AlertDialogComponent, {
              data: {
                title: 'Token vencido',
                acceptLabel: 'Cerrar',
              },
            })
            .afterOpened()
            .subscribe(() => {
              setTimeout(() => {
                this.dialog.closeAll();
              }, 5000);
            });
        }
      });
  }
  recoverPassword() {
    if (this.signInForm.controls.emailControl.valid) {
      this.loading = true;
      this.authService
        .recoverPassword((this.signInForm.controls.emailControl.value as string).trim())
        .subscribe(
          (data) => {
            this.loading = false;
            this.openRecoverPasswordConfirmDialog(false);
          },
          (err) => {
            this.loading = false;
            this.openRecoverPasswordConfirmDialog(true);
          },
        );
    }
  }

  openRecoverPasswordConfirmDialog(isServerError: boolean) {
    if (isServerError) {
      this.dialog.open<AlertDialogComponent, AlertDialogData>(AlertDialogComponent, {
        panelClass: 'modal-container',
        data: {
          title: 'Error recuperando contraseña',
          content: `Hubo un error al recuperar tu contraseña, por favor intentá nuevamente.`,
        },
      });
    } else {
      this.dialog.open<AlertDialogComponent, AlertDialogData>(AlertDialogComponent, {
        panelClass: 'modal-container',
        data: {
          title: '¡Email enviado!',
          content: `Si tenés una cuenta asociada al correo electrónico que ingresaste recibirás las instrucciones por ese medio.`,
        },
      });
    }
  }

  openSignUpView() {
    this.googleAnalyticsService.emitEvent('signup', 'first-use', 'click');
    this.router.navigate(['auth', 'register']);
  }
}
